import { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Container,
  Group,
  Loader,
  Paper,
  ScrollArea,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { Search } from 'lucide-react';
import PageHeader from '@components/shared/PageHeader';
import { useLegacyCatalogMapQuery } from '../hooks/queries/useLegacyCatalogMapQuery';

type Tab = 'courses' | 'videos' | 'questions' | 'unmapped';

const includesQuery = (haystack: string, query: string) =>
  haystack.toLowerCase().includes(query.toLowerCase());

export default function AdminLegacyCatalogMapping() {
  const { data, isLoading, error } = useLegacyCatalogMapQuery();
  const [tab, setTab] = useState<Tab>('courses');
  const [search, setSearch] = useState('');

  const filteredCourses = useMemo(() => {
    if (!data) return [];
    if (!search.trim()) return data.courses;
    return data.courses.filter(
      (row) =>
        includesQuery(String(row.vapor_id), search) ||
        includesQuery(String(row.be_id), search) ||
        includesQuery(row.vapor_name, search) ||
        includesQuery(row.be_name, search)
    );
  }, [data, search]);

  const filteredVideos = useMemo(() => {
    if (!data) return [];
    if (!search.trim()) return data.videos;
    return data.videos.filter(
      (row) =>
        includesQuery(String(row.vapor_id), search) ||
        includesQuery(String(row.be_id), search) ||
        includesQuery(row.vapor_name, search) ||
        includesQuery(row.be_name, search) ||
        includesQuery(row.vapor_category || '', search) ||
        includesQuery(row.be_category || '', search)
    );
  }, [data, search]);

  const filteredQuestions = useMemo(() => {
    if (!data) return [];
    if (!search.trim()) return data.questions;
    return data.questions.filter(
      (row) =>
        includesQuery(String(row.vapor_id), search) ||
        includesQuery(String(row.be_id), search) ||
        includesQuery(String(row.vapor_course_id), search) ||
        includesQuery(String(row.be_course_id), search) ||
        includesQuery(row.match, search)
    );
  }, [data, search]);

  return (
    <Container size='2xl' py='lg'>
      <Stack gap='lg'>
        <PageHeader
          title='Catalog mapping (vapor → be2025)'
          subtitle='Live remap of courses, videos, and questions used by migration. Match is by normalized name (not ID copy).'
          breadcrumbs={[
            { label: 'Admin', href: '/admin/users' },
            { label: 'Legacy Migration', href: '/admin/legacy-migration' },
            { label: 'Mapping' },
          ]}
          backUrl='/admin/legacy-migration'
          actions={
            <Badge color='teal' variant='light'>
              catalog-map
            </Badge>
          }
        />

        {isLoading ? (
          <Group justify='center' py='xl'>
            <Loader />
          </Group>
        ) : error ? (
          <Text c='red'>{(error as Error).message}</Text>
        ) : !data ? (
          <Text c='dimmed'>No catalog map</Text>
        ) : (
          <Stack gap='md'>
            <Alert color='blue' title='How matching works'>
              Courses: strip <code>Corso FAD in</code> + aliases. Videos: base
              name + category (<code>psychology</code> → <code>2d</code>).
              Questions: same id when possible, else description. Unlock videos
              via <code>Videos.product_id</code> (vapor video ids are not
              reused).
            </Alert>

            <Group gap='sm' wrap='wrap'>
              <Badge variant='light'>
                courses {data.summary.courses_mapped}/
                {data.summary.courses_mapped + data.summary.courses_unmapped}
              </Badge>
              <Badge variant='light' color='violet'>
                videos {data.summary.videos_mapped}/
                {data.summary.videos_mapped + data.summary.videos_unmapped}
              </Badge>
              <Badge variant='light' color='grape'>
                questions {data.summary.questions_mapped}/
                {data.summary.questions_mapped +
                  data.summary.questions_unmapped}
              </Badge>
              <Badge variant='outline' color='gray'>
                course id coincidence {data.summary.id_coincidence_courses}
              </Badge>
              <Badge variant='outline' color='orange'>
                video id divergence {data.summary.id_divergence_videos}
              </Badge>
            </Group>

            <Paper withBorder p='md' radius='md'>
              <Group justify='space-between' mb='md' wrap='wrap'>
                <SegmentedControl
                  value={tab}
                  onChange={(value) => setTab(value as Tab)}
                  data={[
                    {
                      label: `Courses (${data.courses.length})`,
                      value: 'courses',
                    },
                    {
                      label: `Videos (${data.videos.length})`,
                      value: 'videos',
                    },
                    {
                      label: `Questions (${data.questions.length})`,
                      value: 'questions',
                    },
                    {
                      label: `Unmapped (${
                        data.unmapped.courses.length +
                        data.unmapped.videos.length +
                        data.unmapped.questions.length
                      })`,
                      value: 'unmapped',
                    },
                  ]}
                />
                <TextInput
                  placeholder='Filter by id or name'
                  leftSection={<Search size={16} />}
                  value={search}
                  onChange={(event) => setSearch(event.currentTarget.value)}
                  style={{ minWidth: 240 }}
                />
              </Group>

              {tab === 'courses' ? (
                <ScrollArea type='auto' offsetScrollbars>
                  <Table striped highlightOnHover withTableBorder stickyHeader>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>vapor id</Table.Th>
                        <Table.Th>vapor name</Table.Th>
                        <Table.Th>be id</Table.Th>
                        <Table.Th>be name</Table.Th>
                        <Table.Th>product</Table.Th>
                        <Table.Th>ids</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {filteredCourses.map((row) => (
                        <Table.Tr key={row.vapor_id}>
                          <Table.Td>{row.vapor_id}</Table.Td>
                          <Table.Td>{row.vapor_name}</Table.Td>
                          <Table.Td>{row.be_id}</Table.Td>
                          <Table.Td>{row.be_name}</Table.Td>
                          <Table.Td>{row.product_id ?? '—'}</Table.Td>
                          <Table.Td>
                            {row.vapor_id === row.be_id ? (
                              <Badge size='xs' color='gray' variant='light'>
                                same id
                              </Badge>
                            ) : (
                              <Badge size='xs' color='orange' variant='light'>
                                remapped
                              </Badge>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              ) : null}

              {tab === 'videos' ? (
                <ScrollArea type='auto' offsetScrollbars>
                  <Table striped highlightOnHover withTableBorder stickyHeader>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>vapor id</Table.Th>
                        <Table.Th>vapor name</Table.Th>
                        <Table.Th>vapor cat</Table.Th>
                        <Table.Th>be video id</Table.Th>
                        <Table.Th>be name</Table.Th>
                        <Table.Th>be cat</Table.Th>
                        <Table.Th>product</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {filteredVideos.map((row) => (
                        <Table.Tr key={row.vapor_id}>
                          <Table.Td>{row.vapor_id}</Table.Td>
                          <Table.Td>{row.vapor_name}</Table.Td>
                          <Table.Td>{row.vapor_category || '—'}</Table.Td>
                          <Table.Td>
                            <Group gap={6} wrap='nowrap'>
                              <Text size='sm'>{row.be_id}</Text>
                              {row.vapor_id !== row.be_id ? (
                                <Badge size='xs' color='orange' variant='light'>
                                  remapped
                                </Badge>
                              ) : null}
                            </Group>
                          </Table.Td>
                          <Table.Td>{row.be_name}</Table.Td>
                          <Table.Td>{row.be_category || '—'}</Table.Td>
                          <Table.Td>{row.product_id ?? '—'}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              ) : null}

              {tab === 'questions' ? (
                <ScrollArea h={520} type='auto' offsetScrollbars>
                  <Table striped highlightOnHover withTableBorder stickyHeader>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>vapor q</Table.Th>
                        <Table.Th>be q</Table.Th>
                        <Table.Th>vapor course</Table.Th>
                        <Table.Th>be course</Table.Th>
                        <Table.Th>match</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {filteredQuestions.map((row) => (
                        <Table.Tr key={row.vapor_id}>
                          <Table.Td>{row.vapor_id}</Table.Td>
                          <Table.Td>{row.be_id}</Table.Td>
                          <Table.Td>{row.vapor_course_id}</Table.Td>
                          <Table.Td>{row.be_course_id}</Table.Td>
                          <Table.Td>
                            <Badge
                              size='xs'
                              color={row.match === 'id' ? 'teal' : 'blue'}
                              variant='light'
                            >
                              {row.match}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              ) : null}

              {tab === 'unmapped' ? (
                <Stack gap='md'>
                  <div>
                    <Title order={5} mb='xs'>
                      Unmapped courses ({data.unmapped.courses.length})
                    </Title>
                    {data.unmapped.courses.length === 0 ? (
                      <Text size='sm' c='dimmed'>
                        None
                      </Text>
                    ) : (
                      <Table striped withTableBorder>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>vapor id</Table.Th>
                            <Table.Th>name</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {data.unmapped.courses.map((row) => (
                            <Table.Tr key={row.vaporId}>
                              <Table.Td>{row.vaporId}</Table.Td>
                              <Table.Td>{row.name}</Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    )}
                  </div>
                  <div>
                    <Title order={5} mb='xs'>
                      Unmapped videos ({data.unmapped.videos.length})
                    </Title>
                    {data.unmapped.videos.length === 0 ? (
                      <Text size='sm' c='dimmed'>
                        None
                      </Text>
                    ) : (
                      <Table striped withTableBorder>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>vapor id</Table.Th>
                            <Table.Th>name</Table.Th>
                            <Table.Th>category</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {data.unmapped.videos.map((row) => (
                            <Table.Tr key={row.vaporId}>
                              <Table.Td>{row.vaporId}</Table.Td>
                              <Table.Td>{row.name}</Table.Td>
                              <Table.Td>{row.category || '—'}</Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    )}
                  </div>
                  <div>
                    <Title order={5} mb='xs'>
                      Unmapped questions ({data.unmapped.questions.length})
                    </Title>
                    {data.unmapped.questions.length === 0 ? (
                      <Text size='sm' c='dimmed'>
                        None
                      </Text>
                    ) : (
                      <ScrollArea h={360} type='auto'>
                        <Table striped withTableBorder>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>vapor id</Table.Th>
                              <Table.Th>course</Table.Th>
                              <Table.Th>description</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {data.unmapped.questions.map((row) => (
                              <Table.Tr key={row.vaporId}>
                                <Table.Td>{row.vaporId}</Table.Td>
                                <Table.Td>{row.courseId}</Table.Td>
                                <Table.Td>{row.description}</Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </ScrollArea>
                    )}
                  </div>
                </Stack>
              ) : null}
            </Paper>
          </Stack>
        )}
      </Stack>
    </Container>
  );
}
